import React, { useId } from 'react';

export interface SwitchProps {
    checked: boolean;
    onChange: (checked: boolean) => void;
    /** Visible name of the setting. Without it, pass `ariaLabel`. */
    label?: React.ReactNode;
    /** One line under the label: what turning it on does. */
    description?: React.ReactNode;
    /** Word next to the switch; defaults to «Activado» / «Desactivado».
     *  That default is Spanish, right only for the Admin: in the tenant pass a
     *  translated word or `hideStateLabel` (`lint:components` checks it). */
    stateLabel?: React.ReactNode;
    /** Hide the state word (dense rows that already say it elsewhere). */
    hideStateLabel?: boolean;
    disabled?: boolean;
    ariaLabel?: string;
    className?: string;
}

/**
 * On/off switch for a yes/no setting. The only sanctioned control for a
 * boolean that takes effect on save: never a text field where someone types
 * `true`, never a dropdown with «true — activado».
 *
 * For a choice between two NAMED options (Filamento / Resina) use
 * `BinaryToggle`: that one says which side is chosen, this one says whether
 * something is on.
 *
 * The label row is a `<label>`-like click target: clicking the text flips the
 * switch too. `role="switch"` + `aria-checked` is what screen readers expect.
 */
const Switch: React.FC<SwitchProps> = ({
    checked,
    onChange,
    label,
    description,
    stateLabel,
    hideStateLabel = false,
    disabled = false,
    ariaLabel,
    className = '',
}) => {
    const id = useId();
    const flip = () => {
        if (!disabled) onChange(!checked);
    };
    const button = (
        <button
            type="button"
            role="switch"
            id={id}
            aria-checked={checked}
            aria-label={label ? undefined : ariaLabel}
            aria-labelledby={label ? `${id}-label` : undefined}
            aria-describedby={description ? `${id}-desc` : undefined}
            disabled={disabled}
            onClick={flip}
            className={`relative inline-flex h-6 w-11 shrink-0 items-center rounded-full transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/50 focus-visible:ring-offset-2 focus-visible:ring-offset-white dark:focus-visible:ring-offset-surface-dark ${
                checked ? 'bg-primary' : 'bg-slate-300 dark:bg-slate-600'
            } ${disabled ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}`}
        >
            <span
                aria-hidden="true"
                className={`inline-block h-5 w-5 rounded-full bg-white shadow transition-transform ${
                    checked ? 'translate-x-[22px]' : 'translate-x-0.5'
                }`}
            />
        </button>
    );
    const state = hideStateLabel ? null : (
        <span className={`text-xs font-bold uppercase tracking-wider whitespace-nowrap ${checked ? 'text-primary' : 'text-text-sub dark:text-slate-400'}`}>
            {stateLabel ?? (checked ? 'Activado' : 'Desactivado')}
        </span>
    );

    if (!label && !description) {
        return (
            <span className={`inline-flex items-center gap-2 ${className}`}>
                {button}
                {state}
            </span>
        );
    }

    return (
        <div className={`flex items-start justify-between gap-4 ${className}`}>
            <div className="min-w-0 flex flex-col gap-0.5">
                {label ? (
                    <span
                        id={`${id}-label`}
                        onClick={flip}
                        className={`text-sm font-semibold text-text-main dark:text-slate-100 ${disabled ? '' : 'cursor-pointer'}`}
                    >
                        {label}
                    </span>
                ) : null}
                {description ? (
                    <span id={`${id}-desc`} className="text-xs text-text-sub dark:text-slate-400 leading-relaxed">
                        {description}
                    </span>
                ) : null}
            </div>
            <span className="inline-flex items-center gap-2 pt-0.5">
                {state}
                {button}
            </span>
        </div>
    );
};

export default Switch;
